import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { useNavigationSuspenseQuery } from '../api/portalGateway/navigation';
import { InactivityObserver } from '../components/inactivityobserver/InactivityObserver';
import { LegacyUrlHandler } from '../components/legacyurlhandler/LegacyUrlHandler';
import { UserRolesLoader } from '../components/userrolesloader/UserRolesLoader';
import { ROUTE_BASE_NAME } from '../config';
import i18n from '../i18n/config';
import { ChangePassword as ChangePasswordScene } from '../scenes/ChangePassword';
import { Disclaimer } from '../scenes/Disclaimer';
import { Login as LoginScene } from '../scenes/Login';
import { Main as MainScene } from '../scenes/Main';
import { useStore } from '../store';
import { pageWithRoleLink } from '../utils/urlPath';

import App from './app';

const RequireLogin = ({
  changePasswordCheck = true,
}: {
  changePasswordCheck?: boolean;
}) => {
  const [isAuthTokenExpired, session] = useStore(
    useShallow((state) => [
      state.session?.isAuthTokenExpired() ?? true,
      state.session,
    ])
  );

  if (!session || isAuthTokenExpired) {
    return <Navigate to="/login" />;
  }

  if (changePasswordCheck && session?.hasToChangePassword()) {
    return <Navigate to="/changeInitialPassword" />;
  }

  return <Outlet />;
};

function NavigateToFirstPage() {
  const { data: navigationData } = useNavigationSuspenseQuery();
  const role = useStore((state) => state.activeUserRole);

  return role != null ? (
    <Navigate
      to={pageWithRoleLink(role.id, {
        slug: navigationData.initialPageSlug,
      })}
    />
  ) : (
    <MainScene pageType="page" />
  );
}

export const routes = [
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: '/login',
        element: <LoginScene />,
      },
      {
        path: '/changeInitialPassword',
        element: <RequireLogin changePasswordCheck={false} />,
        children: [
          {
            index: true,
            element: <ChangePasswordScene forcedChange={true} />,
          },
        ],
      },
      {
        path: '/changePassword',
        element: <RequireLogin />,
        children: [
          {
            index: true,
            element: <ChangePasswordScene forcedChange={false} />,
          },
        ],
      },
      // routes requiring login
      {
        path: '/',
        element: <RequireLogin />,
        children: [
          {
            element: <InactivityObserver />,
            children: [
              {
                element: <Disclaimer />,
                children: [
                  {
                    element: <LegacyUrlHandler />,
                    children: [
                      {
                        element: <UserRolesLoader />,
                        children: [
                          {
                            index: true,
                            element: <NavigateToFirstPage />,
                          },
                          {
                            path: '/p',
                            element: <NavigateToFirstPage />,
                          },
                          {
                            path: '/p/:roleId',
                            element: <NavigateToFirstPage />,
                          },
                          {
                            path: '/p/:roleId/*',
                            element: <MainScene pageType="page" />,
                          },
                          {
                            path: '/dp/:roleId/*',
                            element: <MainScene pageType="detailPage" />,
                          },
                          { path: '*', element: <RequireLogin /> },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      // 404
      {
        path: '*',
        element: <div>{i18n.t('app.page.pageNotFoundError')}</div>,
      },
    ],
  },
];

export const router = createBrowserRouter(routes, {
  basename: ROUTE_BASE_NAME,
});
